/**
 * Avvisi di sessione condivisi da TUTTE le app embedded (React e vanilla JS).
 *
 * Due pezzi, entrambi senza dipendenze da React:
 *  1. il catalogo dei messaggi (`EMBEDDED_SESSION_NOTICES`): l'unica fonte del testo, costruita
 *     su `embeddedSessionWaitingBannerMessage`. Le app NON scrivono a mano questi testi.
 *  2. `mountEmbeddedSessionNotice`: la barra DOM per le app vanilla (Pixel Art, ScratchJr).
 *     Le app React usano `EmbeddedSessionNoticeBar` / `EmbeddedSessionWaitingBanner`
 *     (`@tl4k/admin-ui`), che leggono lo stesso catalogo e gli stessi token colore.
 *
 * Regola: `docs/standards/TL4K_EMBEDDED_APP_BOOT.md` § UI attesa. In TechyWeb
 * `TL4KAppHeader` e' nascosto: lo stato della sessione lo dice solo questa barra.
 */
import { embeddedSessionWaitingBannerMessage } from "./resolveAppSessionLaunch";
import { teacherSessionGoneNotice, teacherSessionNetworkNotice } from "./teacherSessionFallback";
import { sessionWinsNotice, wrongSessionTypePinNotice } from "./nonBlockingSessionWait";

export type EmbeddedSessionNoticeKind = "waiting" | "joined" | "info" | "warning" | "error";

/** App con salvataggio a richiesta: la barra di attesa nomina il pulsante da usare. */
export interface ManualSaveHint {
  /** Etichetta del pulsante, es. `Salva nel Cloud`. */
  action: string;
  /** Cosa si manda nella sessione, con articolo: `il disegno`, `la storia`. */
  what: string;
}

/**
 * Testo di attesa del docente. Senza `manualSave` e' il testo standard (salvataggio
 * automatico all'ingresso in sessione); con `manualSave` dice quale pulsante usare.
 */
export function embeddedSessionWaitingMessage(appLabel: string, manualSave?: ManualSaveHint): string {
  if (!manualSave) return embeddedSessionWaitingBannerMessage(appLabel);
  return `Aspetta che l'insegnante apra ${appLabel}: intanto puoi lavorare. Quando la sessione parte, usa «${manualSave.action}» per mandare ${manualSave.what} nella sessione.`;
}

export const EMBEDDED_SESSION_NOTICES = {
  /** Il docente non ha ancora aperto l'app: lavoro locale, si salva all'ingresso. */
  waiting: embeddedSessionWaitingMessage,
  /** Lo studente e' entrato nella sessione derivata. */
  joined: "Sei nella sessione: il lavoro viene salvato.",
  /** La sessione aveva gia' un elaborato: vince quello, la copia locale resta sul dispositivo. */
  localCopyKept: sessionWinsNotice,
  /** PIN che non e' una sessione di questa app (es. PIN incontro passato come PIN app). */
  wrongPinType: wrongSessionTypePinNotice,
  /** La sessione e' chiusa, scaduta o inesistente (`reason` del by-pin: closed|expired|not_found). */
  sessionEnded: teacherSessionGoneNotice,
  /** Rete assente: il lavoro resta sul dispositivo. */
  network: teacherSessionNetworkNotice,
  /** L'ingresso in sessione e' rifiutato (4xx): inutile riprovare. */
  cannotJoin: "Non riesco a entrare nella sessione della classe.",
  /** Lo studente non e' riconosciuto (manca lo pseudonimo): serve riaprire da TechyWeb. */
  unrecognized: "Non riesco a riconoscerti: riapri l'app da TechyWeb.",
} as const;

export type EmbeddedSessionNoticeColors = { background: string; border: string; color: string };

/** Colori per tipo di avviso: SOLO token `--tl4k-*` (identici in React e nel DOM vanilla). */
export function embeddedSessionNoticeColors(kind: EmbeddedSessionNoticeKind): EmbeddedSessionNoticeColors {
  switch (kind) {
    case "joined":
      return {
        background: "var(--tl4k-success-surface, Canvas)",
        border: "var(--tl4k-success, GrayText)",
        color: "var(--tl4k-neutral-800, CanvasText)",
      };
    case "error":
      return {
        background: "var(--tl4k-error-surface, Canvas)",
        border: "var(--tl4k-error, GrayText)",
        color: "var(--tl4k-neutral-800, CanvasText)",
      };
    case "info":
      return {
        background: "var(--tl4k-info-surface, Canvas)",
        border: "var(--tl4k-info, GrayText)",
        color: "var(--tl4k-neutral-800, CanvasText)",
      };
    case "waiting":
    case "warning":
    default:
      return {
        background: "var(--tl4k-warning-surface, Canvas)",
        border: "var(--tl4k-warning, GrayText)",
        color: "var(--tl4k-neutral-800, CanvasText)",
      };
  }
}

const NOTICE_CLASS = "tl4k-session-notice";
const STYLE_ID = "tl4k-session-notice-style";

/** Stile della barra: solo token, nessun colore o font fisso. Una sola volta per documento. */
const NOTICE_CSS = `
.${NOTICE_CLASS} {
  box-sizing: border-box;
  width: 100%;
  display: flex;
  align-items: center;
  gap: var(--tl4k-spacing-sm);
  flex: 0 0 auto;
  padding: var(--tl4k-spacing-xs) var(--tl4k-spacing-md);
  font-family: var(--tl4k-font-family-body);
  font-size: var(--tl4k-font-size-small);
  line-height: 1.35;
  border-bottom: 1px solid;
  position: relative;
  z-index: 1500;
}
.${NOTICE_CLASS}__text { flex: 1 1 auto; min-width: 0; overflow-wrap: anywhere; }
.${NOTICE_CLASS}__spinner {
  flex: 0 0 auto;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid currentColor;
  border-top-color: transparent;
  animation: ${NOTICE_CLASS}-spin 0.9s linear infinite;
}
.${NOTICE_CLASS}__close {
  flex: 0 0 auto;
  min-height: 32px;
  padding: 0 var(--tl4k-spacing-sm);
  font: inherit;
  font-weight: 600;
  color: inherit;
  background: transparent;
  border: 1px solid currentColor;
  border-radius: var(--tl4k-border-radius-md);
  cursor: pointer;
}
@keyframes ${NOTICE_CLASS}-spin { to { transform: rotate(360deg); } }
@media (prefers-reduced-motion: reduce) { .${NOTICE_CLASS}__spinner { animation: none; } }
`;

export interface MountEmbeddedSessionNoticeOptions {
  kind: EmbeddedSessionNoticeKind;
  message: string;
  /** Se presente compare «Chiudi»; chiude la barra e poi chiama la callback. */
  onDismiss?: () => void;
  testId?: string;
}

export interface EmbeddedSessionNoticeHandle {
  /** Cambia testo/tipo senza rimontare. */
  update: (next: { kind?: EmbeddedSessionNoticeKind; message?: string }) => void;
  remove: () => void;
  readonly element: HTMLElement;
}

function ensureStyle(doc: Document): void {
  if (doc.getElementById(STYLE_ID)) return;
  const style = doc.createElement("style");
  style.id = STYLE_ID;
  style.textContent = NOTICE_CSS;
  (doc.head ?? doc.documentElement).appendChild(style);
}

function paint(
  el: HTMLElement,
  textEl: HTMLElement,
  spinner: HTMLElement,
  kind: EmbeddedSessionNoticeKind,
  message: string,
): void {
  const colors = embeddedSessionNoticeColors(kind);
  el.style.background = colors.background;
  el.style.borderBottomColor = colors.border;
  el.style.color = colors.color;
  el.setAttribute("role", kind === "error" ? "alert" : "status");
  el.setAttribute("aria-live", kind === "error" ? "assertive" : "polite");
  el.dataset.kind = kind;
  spinner.hidden = kind !== "waiting";
  textEl.textContent = message;
}

/**
 * Monta (o aggiorna) l'unico avviso di sessione dentro `container`, in cima. Un solo avviso per
 * contenitore: richiamarla sostituisce il precedente. Funziona nell'iframe di TechyWeb
 * (nessun `TL4KAppHeader`). Il testo e' sempre `textContent`: mai HTML.
 */
export function mountEmbeddedSessionNotice(
  container: HTMLElement,
  options: MountEmbeddedSessionNoticeOptions,
): EmbeddedSessionNoticeHandle {
  const doc = container.ownerDocument;
  ensureStyle(doc);
  container.querySelectorAll(`:scope > .${NOTICE_CLASS}`).forEach((n) => n.remove());

  const el = doc.createElement("div");
  el.className = NOTICE_CLASS;
  el.dataset.testid = options.testId ?? "session-notice";
  el.setAttribute("data-testid", options.testId ?? "session-notice");

  const spinner = doc.createElement("span");
  spinner.className = `${NOTICE_CLASS}__spinner`;
  spinner.setAttribute("aria-hidden", "true");
  const textEl = doc.createElement("span");
  textEl.className = `${NOTICE_CLASS}__text`;
  el.append(spinner, textEl);

  const handle: EmbeddedSessionNoticeHandle = {
    element: el,
    update(next) {
      paint(
        el,
        textEl,
        spinner,
        next.kind ?? (el.dataset.kind as EmbeddedSessionNoticeKind),
        next.message ?? textEl.textContent ?? "",
      );
    },
    remove() {
      el.remove();
    },
  };

  if (options.onDismiss) {
    const close = doc.createElement("button");
    close.type = "button";
    close.className = `${NOTICE_CLASS}__close`;
    close.setAttribute("aria-label", "Chiudi avviso");
    close.textContent = "Chiudi";
    close.addEventListener("click", () => {
      handle.remove();
      options.onDismiss?.();
    });
    el.appendChild(close);
  }

  paint(el, textEl, spinner, options.kind, options.message);
  container.prepend(el);
  return handle;
}
